// SAD-461 (LIB-04) — TanStack Query bindings for release + live / adopted /
// frozen reuse. The server decides what each caller may see; nothing here
// caches content across viewers beyond the query key.
import { ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { libraryReleaseClient } from './reuseClient';
export const RECEIVING_OBJECT_TYPE = 'response';
export const TRANSFER_MODES = [
    {
        value: 'live_reference',
        label: 'Keep in sync with the source'
    },
    {
        value: 'adopted',
        label: 'Make an editable copy'
    },
    {
        value: 'frozen_snapshot',
        label: 'Copy as it is now'
    }
];
const SELECTABLE_KEY = 'library-selectable-releases';
const REUSES_KEY = 'library-reuses';
export function errorText(err) {
    return err instanceof ConnectError ? err.rawMessage : String(err);
}
/** Releases the viewer's audience membership reaches (no content). */ export function useSelectableReleases(enabled) {
    const query = useQuery({
        gcTime: 0,
        retry: false,
        queryKey: [
            SELECTABLE_KEY
        ],
        enabled,
        queryFn: async ()=>{
            try {
                return {
                    rows: (await libraryReleaseClient.listSelectableReleases({})).releases,
                    error: false
                };
            } catch  {
                return {
                    rows: [],
                    error: true
                };
            }
        }
    });
    return {
        ...query,
        data: query.data?.rows,
        isError: query.isError || !!query.data?.error
    };
}
export function useLibraryReuses(objectId) {
    const query = useQuery({
        gcTime: 0,
        retry: false,
        queryKey: [
            REUSES_KEY,
            objectId
        ],
        enabled: objectId !== '',
        queryFn: async ()=>{
            try {
                return {
                    rows: (await libraryReleaseClient.listLibraryReuses({
                        receivingObjectType: RECEIVING_OBJECT_TYPE,
                        receivingObjectId: objectId
                    })).reuses,
                    error: false
                };
            } catch  {
                return {
                    rows: [],
                    error: true
                };
            }
        }
    });
    return {
        ...query,
        data: query.data?.rows,
        isError: query.isError || !!query.data?.error
    };
}
export function useReleaseItem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (a)=>libraryReleaseClient.releaseLibraryItem({
                externalOutputEligibility: a.externalOutputEligibility,
                externalOutputRestrictions: a.externalOutputRestrictions,
                restrictions: a.restrictions,
                libraryItemId: a.libraryItemId,
                workingVersionId: a.workingVersionId,
                releasedTitle: a.releasedTitle,
                releasedContent: a.releasedContent,
                releasedContentFormat: 'plain_text',
                audiences: a.audienceScopeNodeIds.map((id)=>({
                        kind: 'scope_node',
                        scopeNodeId: id,
                        principalGroupId: ''
                    }))
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    SELECTABLE_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    REUSES_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'library-source-change'
                ]
            });
        }
    });
}
export function useWithdrawRelease() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (a)=>libraryReleaseClient.withdrawLibraryRelease(a),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    SELECTABLE_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    REUSES_KEY
                ]
            });
        }
    });
}
export function useCreateReuse() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (a)=>libraryReleaseClient.createLibraryReuse({
                releaseId: a.releaseId,
                transferMode: a.mode,
                receivingObjectType: RECEIVING_OBJECT_TYPE,
                receivingObjectId: a.objectId
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    REUSES_KEY
                ]
            })
    });
}
export function useEditAdopted() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (a)=>libraryReleaseClient.editAdoptedLibraryReuse(a),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    REUSES_KEY
                ]
            })
    });
}
